/**
 * The active organization - ours, not Clerk's.
 *
 * Clerk authenticates; organizations, memberships and roles live in
 * NimblyBase. The active org is remembered per browser and sent to the API as
 * X-Organization-Id, where a membership lookup decides whether it is
 * honoured. Nothing here is a security boundary - the server's membership
 * check is.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { createApi, type Organization, type PendingInvitation } from './api.js'

const STORAGE_KEY = 'nimblybase.activeOrgId'

type OrgContextValue = {
  loading: boolean
  /**
   * The organization list could not be loaded. Distinct from "no
   * organizations": a user with three orgs whose API call failed must see
   * an error, not an invitation to create a fourth.
   */
  loadError: string | null
  organizations: Organization[]
  active: Organization | null
  invitations: PendingInvitation[]
  switchTo: (orgId: string) => void
  refresh: () => Promise<void>
  createOrganization: (name: string) => Promise<Organization>
  acceptInvitation: (id: string) => Promise<void>
}

const OrgContext = createContext<OrgContextValue | null>(null)

export function useOrgs(): OrgContextValue {
  const value = useContext(OrgContext)
  if (!value) throw new Error('useOrgs must be used inside OrgProvider')
  return value
}

/** The api used by pages - it sends the active org automatically. */
export function useOrgApi() {
  const { getToken } = useAuth()
  const { active } = useOrgs()
  return useMemo(
    () => createApi(() => getToken(), () => active?.id ?? null),
    [getToken, active?.id],
  )
}

function remember(orgId: string | null): void {
  try {
    if (orgId) localStorage.setItem(STORAGE_KEY, orgId)
    else localStorage.removeItem(STORAGE_KEY)
  } catch { /* storage can be unavailable; the selection just won't persist */ }
}

function remembered(): string | null {
  try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuth()
  // Org-listing calls carry no org header - they must work before one exists.
  const bare = useMemo(() => createApi(() => getToken()), [getToken])

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [invitations, setInvitations] = useState<PendingInvitation[]>([])
  const [activeId, setActiveId] = useState<string | null>(remembered())

  const refresh = useCallback(async () => {
    let orgs: { organizations: Organization[] }
    let pending: { invitations: PendingInvitation[] }
    try {
      ;[orgs, pending] = await Promise.all([
        bare.listOrganizations(),
        bare.myInvitations(),
      ])
    } catch (err) {
      // Keep whatever was loaded before; say the load failed. Rendering an
      // empty list here is what showed onboarding to a signed-in owner while
      // the environment was asleep.
      setLoadError(err instanceof Error ? err.message : 'Could not reach the API.')
      return
    }
    setLoadError(null)
    setOrganizations(orgs.organizations)
    setInvitations(pending.invitations)

    // A remembered selection that no longer matches a membership is dropped
    // rather than sent - the server would refuse it anyway, but silently
    // acting in "no org" while showing a stale name would be worse.
    setActiveId((current) => {
      const valid = orgs.organizations.some((o) => o.id === current)
      if (valid) return current
      const first = orgs.organizations[0]?.id ?? null
      remember(first)
      return first
    })
  }, [bare])

  useEffect(() => {
    refresh().finally(() => setLoading(false))
  }, [refresh])

  const switchTo = useCallback((orgId: string) => {
    setActiveId(orgId)
    remember(orgId)
  }, [])

  const createOrganization = useCallback(async (name: string) => {
    const { organization } = await bare.createOrganization({ name })
    await refresh()
    switchTo(organization.id)
    return organization
  }, [bare, refresh, switchTo])

  const acceptInvitation = useCallback(async (id: string) => {
    const { organization } = await bare.redeemInvitation(id)
    await refresh()
    switchTo(organization.id)
  }, [bare, refresh, switchTo])

  const active = organizations.find((o) => o.id === activeId) ?? null

  return (
    <OrgContext.Provider value={{
      loading, loadError, organizations, active, invitations,
      switchTo, refresh, createOrganization, acceptInvitation,
    }}>
      {children}
    </OrgContext.Provider>
  )
}
